import { LightningElement, api } from "lwc";

const SELECTOR_ROOT = "root";
const SELECTOR_INPUT = "input";
const SELECTOR_OPTIONS = "options";
const SELECTOR_OPTION = "option";
function getSelector(selector) {
  return `[data-listbox="${selector}"]`;
}
class Listbox {
  _count = 0;
  _state = {
    active: false,
    selectedIndex: -1,
    setup: false,
  };

  get state() {
    return this._state;
  }

  set state(value) {
    this._state = value;
    this.update();
    this.input.onChange(this._state);
  }

  get isExpanded() {
    return this.state.active && this._count > 0;
  }

  hover = false;

  constructor(input) {
    this.input = input;
    input.onChange(this.state);
  }

  setState(nextState) {
    let { state } = this;
    this.state = {
      ...state,
      ...(typeof nextState === "function" ? nextState(state) : nextState),
    };
  }

  renderedCallback() {
    if (!this.state.setup) {
      let $input = this.getInputElement();
      let $options = this.getElement(SELECTOR_OPTIONS);
      $input.addEventListener("blur", this.onBlur);
      $input.addEventListener("focus", this.onFocus);
      $input.addEventListener("keydown", this.onKeyDown);
      $options.addEventListener("click", this.onClick);
      $options.addEventListener("mouseenter", this.onMouseEnter);
      $options.addEventListener("mouseleave", this.onMouseLeave);
      this.state.setup = true;
    }

    this._count = this.input.shadowRoot.querySelectorAll(
      getSelector(SELECTOR_OPTION)
    ).length;
    this.update();
  }

  disconnectedCallback() {
    let $input = this.getInputElement();
    let $options = this.getElement(SELECTOR_OPTIONS);

    if ($input) {
      $input.removeEventListener("blur", this.onBlur);
      $input.removeEventListener("focus", this.onFocus);
      $input.removeEventListener("keydown", this.onKeyDown);
    }

    if ($options) {
      $options.removeEventListener("click", this.onClick);
      $options.removeEventListener("mouseenter", this.onMouseEnter);
      $options.removeEventListener("mouseleave", this.onMouseLeave);
    }
  }

  getElement(selector) {
    return this.input.shadowRoot.querySelector(getSelector(selector));
  }

  getInputElement() {
    let el = this.getElement(SELECTOR_INPUT);

    if (el.nodeName.toLowerCase() === "tds-input") {
      return el.shadowRoot.querySelector("input");
    }

    return el;
  }

  onClick = (e) => {
    let selectedIndex = Array.from(
      this.input.shadowRoot.querySelectorAll(getSelector(SELECTOR_OPTION))
    ).findIndex((n) => n.contains(e.target));
    this.setState({
      active: false,
      selectedIndex,
    });
    this.input.onSelect(this.state.selectedIndex);
  };
  onBlur = () => {
    if (!this.hover) {
      this.setState({
        active: false,
      });
    }
  };
  onFocus = () => {
    this.setState({
      active: true,
      selectedIndex: -1,
    });
  };
  onKeyDown = (e) => {
    switch (e.key) {
      case "ArrowUp":
        this.setState((state) => {
          let { selectedIndex } = state;
          selectedIndex--;
          if (selectedIndex < 0) selectedIndex = this._count - 1;
          return {
            selectedIndex,
          };
        });
        break;

      case "ArrowDown":
        this.setState((state) => {
          let { selectedIndex } = state;
          selectedIndex++;
          if (selectedIndex === this._count) selectedIndex = 0;
          return {
            selectedIndex,
          };
        });
        break;

      case "Enter":
        this.setState({
          active: false,
        });

        if (this.state.selectedIndex > -1) {
          this.input.onSelect(this.state.selectedIndex);
        } else {
          this.input.onSubmit();
        }

        break;
    }
  };
  onMouseEnter = () => {
    this.hover = true;
  };
  onMouseLeave = () => {
    this.hover = false;
  };

  setRootAttributes() {
    let $root = this.getElement(SELECTOR_ROOT);
    $root.setAttribute("aria-expanded", String(this.isExpanded));
    $root.setAttribute("aria-haspopup", "listbox");
    $root.setAttribute("role", "combobox");
  }

  setInputAttributes() {
    let $activeDecendant = this.input.shadowRoot.querySelectorAll(
      getSelector(SELECTOR_OPTION)
    )[this.state.selectedIndex];
    let $input = this.getInputElement();
    let $options = this.getElement(SELECTOR_OPTIONS);
    $input.setAttribute(
      "aria-activedescendant",
      $activeDecendant ? $activeDecendant.getAttribute("id") || "" : ""
    );
    $input.setAttribute("aria-autocomplete", "list");
    $input.setAttribute("aria-controls", $options.getAttribute("id") || "");
    $input.setAttribute("autocomplete", "off");
  }

  setListboxAttributes() {
    let $options = this.getElement(SELECTOR_OPTIONS);
    $options.setAttribute("role", "listbox");
    $options.setAttribute("aria-hidden", String(!this.isExpanded));
    this.input.shadowRoot.querySelectorAll("ui,li").forEach((node) => {
      let element = node;
      if (element.hasAttribute("role")) return;
      element.setAttribute("role", "presentation");
    });
  }

  setListboxItemAttributes() {
    this.input.shadowRoot
      .querySelectorAll(getSelector(SELECTOR_OPTION))
      .forEach((node, i) => {
        let element = node;
        element.setAttribute(
          "aria-selected",
          String(i === this.state.selectedIndex)
        );
        element.setAttribute("role", "option");
      });
  }

  update() {
    this.setRootAttributes();
    this.setInputAttributes();
    this.setListboxAttributes();
    this.setListboxItemAttributes();
  }
}

class search extends LightningElement {
  @api completions = [];
  @api defaultValue = null;
  @api placeholder = "Search";
  @api recents = null;

  @api get value() {
    return this.template.querySelector("tds-input").value;
  }

  completionTerms = null;
  setup = false;
  listbox = new Listbox({
    onChange: (state) => {
      this.listboxState = state;
    },
    onSelect: (index) => {
      let { completions, hasCompletions, hasRecents, recents } = this;

      if (hasCompletions) {
        this.dispatchSearchEvent({
          type: "completion",
          value: completions[index],
        });
      } else if (hasRecents) {
        this.dispatchSearchEvent({
          type: "recent",
          value: recents.flatMap((s) => s.items)[index],
        });
      }
    },
    onSubmit: () => {
      let { completionTerms } = this;
      if (!completionTerms) return;
      this.dispatchSearchEvent({
        type: "input",
        value: completionTerms,
      });
    },
    shadowRoot: this.template,
  });
  listboxState = null;

  get hasCompletions() {
    return this.completions && this.completions.length > 0;
  }

  get hasRecents() {
    return this.recents && this.recents.length > 0;
  }

  get recentsWithStyle() {
    return this.recents.map((section) =>
      Object.assign({}, section, {
        items: section.items.map((item) =>
          Object.assign({}, item, {
            iconStyle: item.iconBackgroundColor
              ? `background-color: ${item.iconBackgroundColor};`
              : "",
          })
        ),
      })
    );
  }

  dispatchSearchEvent(event) {
    this.dispatchEvent(
      new CustomEvent("search", {
        detail: event,
      })
    );
  }

  onInput(e) {
    let { value } = e.currentTarget;
    let terms = value.trim();
    this.completionTerms = terms;
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: {
          value: terms,
        },
      })
    );
  }

  get showRecents() {
    let { completionTerms, hasCompletions, hasRecents, listboxState } = this;
    if (!listboxState) return false;
    if (completionTerms && completionTerms.length) return false;
    return listboxState.active && hasRecents && !hasCompletions;
  }

  get showCompletions() {
    let { hasCompletions, listboxState } = this;
    if (!listboxState) return false;
    return listboxState.active && hasCompletions;
  }

  renderedCallback() {
    if (!this.setup) {
      this.setup = true;

      if (this.defaultValue) {
        let input = this.template.querySelector(".search-input__input");
        input.value = this.defaultValue;
        this.completionTerms = this.defaultValue;
      }
    }

    this.listbox.renderedCallback();
  }

  disconnectedCallback() {
    this.listbox.disconnectedCallback();
  }
}

export default search;
